import { useState, useEffect } from "react";
import { Brain, Plus, Trash2, Eye, EyeOff, RotateCcw, Check, X, Star } from "lucide-react";
import PageShell from "../components/PageShell";
import {
  getMemoryVerses, addMemoryVerse, deleteMemoryVerse,
  reviewMemoryVerse, getDueMemoryVerses,
} from "../lib/personal";
import { getBooks, getChapter } from "../api";
import { POPULAR_VERSES } from "../data/popularVerses";
import { useSettings } from "../context/SettingsContext";
import { track } from "../lib/analytics";
import TranslationToggle from "../components/TranslationToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const firstLetters = (text) =>
  text
    .split(/\s+/)
    .map((w) => w.replace(/([A-Za-z])([A-Za-z]*)/g, (_, first, rest) => first + "_".repeat(rest.length)))
    .join(" ");

export default function Memorize() {
  const { translation } = useSettings();
  const [verses, setVerses] = useState(getMemoryVerses());
  const [open, setOpen] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  const [adding, setAdding] = useState(null);
  const [books, setBooks] = useState([]);
  const [pBook, setPBook] = useState("JHN");
  const [pChapter, setPChapter] = useState(3);
  const [pVerses, setPVerses] = useState([]);
  const [pBookName, setPBookName] = useState("John");
  const [practice, setPractice] = useState(null); // {queue, idx}
  const [revealed, setRevealed] = useState(false);
  const [hint, setHint] = useState(false);

  const refresh = () => setVerses(getMemoryVerses());
  const due = getDueMemoryVerses();

  useEffect(() => {
    if (open && books.length === 0) getBooks().then(setBooks);
  }, [open, books.length]);

  useEffect(() => {
    if (!open) return;
    getChapter(pBook, pChapter, translation).then((d) => { setPVerses(d.verses); setPBookName(d.book_name); }).catch(() => setPVerses([]));
  }, [open, pBook, pChapter, translation]);

  const curBook = books.find((b) => b.id === pBook);

  const addFromPicker = (v) => {
    const added = addMemoryVerse({ ref: `${pBookName} ${pChapter}:${v.v}`, text: v.t, book: pBook, chapter: pChapter, verse: v.v, translation });
    if (added) { track("verse_saved", { ref: `${pBookName} ${pChapter}:${v.v}`, source: "picker", translation }); toast.success(`Added ${pBookName} ${pChapter}:${v.v} (${translation})`); refresh(); }
    else toast.info("Already in your verses");
  };

  const haveRef = (ref) => verses.some((m) => m.ref === ref);
  const addPopular = async (p) => {
    if (haveRef(p.ref)) { toast.info("Already in your verses"); return; }
    setAdding(p.ref);
    try {
      const d = await getChapter(p.book, p.chapter, translation);
      const end = p.verseEnd || p.verse;
      const picked = d.verses.filter((v) => v.v >= p.verse && v.v <= end);
      if (!picked.length) throw new Error("not found");
      const text = picked.map((v) => v.t).join(" ");
      const added = addMemoryVerse({ ref: p.ref, text, book: p.book, chapter: p.chapter, verse: p.verse, translation });
      if (added) { track("verse_saved", { ref: p.ref, source: "popular", translation }); toast.success(`Added ${p.ref} (${translation})`); refresh(); }
      else toast.info("Already in your verses");
    } catch {
      toast.error("Couldn't add that verse");
    } finally {
      setAdding(null);
    }
  };

  const startPractice = (queue) => {
    if (!queue || queue.length === 0) return;
    setRevealed(false);
    setHint(false);
    setPractice({ queue, idx: 0 });
  };
  const grade = (correct) => {
    const v = practice.queue[practice.idx];
    reviewMemoryVerse(v.id, correct);
    refresh();
    const nextIdx = practice.idx + 1;
    if (nextIdx >= practice.queue.length) setPractice(null);
    else { setPractice({ ...practice, idx: nextIdx }); setRevealed(false); setHint(false); }
  };

  if (practice) {
    const v = practice.queue[practice.idx];
    return (
      <PageShell title="Practice" subtitle={`${practice.idx + 1} of ${practice.queue.length}`} icon={Brain}
        accent={{ a: "#9C7BB0", b: "#4A5C8C", c: "#2C2A55" }} testid="practice-page">
        <button onClick={() => setPractice(null)} className="font-ui mb-6 text-sm text-muted-foreground hover:text-foreground" data-testid="end-practice">
          ← End session
        </button>
        <div className="rounded-3xl border border-border bg-background/50 p-8 backdrop-blur-md md:p-12" data-testid="practice-card">
          <p className="font-ui mb-6 text-sm font-semibold uppercase tracking-[0.2em] text-primary">{v.ref}</p>
          <p className="reading-text min-h-[8rem] font-serif-display text-2xl leading-relaxed md:text-3xl">
            {revealed ? v.text : hint ? firstLetters(v.text) : "• • •  Recite this verse from memory  • • •"}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setHint((h) => !h)} data-testid="hint-btn"
              className="font-ui flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm transition-colors hover:bg-muted">
              {hint ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} First letters
            </button>
            <button onClick={() => setRevealed(true)} data-testid="reveal-btn"
              className="font-ui flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm transition-colors hover:bg-muted">
              <RotateCcw className="h-4 w-4" /> Reveal
            </button>
          </div>
          {revealed && (
            <div className="mt-8 flex gap-3">
              <button onClick={() => grade(false)} data-testid="grade-missed"
                className="font-ui flex flex-1 items-center justify-center gap-2 rounded-full border border-border py-3.5 text-sm font-semibold transition-colors hover:bg-muted">
                <X className="h-4 w-4" /> Missed it
              </button>
              <button onClick={() => grade(true)} data-testid="grade-got"
                className="font-ui flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">
                <Check className="h-4 w-4" /> Got it!
              </button>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Verse Memorization" subtitle={`${verses.length} verses · ${due.length} due today`} icon={Brain}
      accent={{ a: "#9C7BB0", b: "#4A5C8C", c: "#2C2A55" }} testid="memorize-page">
      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="memorize-translation-row">
        <span className="font-ui text-xs font-semibold uppercase tracking-wide text-muted-foreground">Memorize in</span>
        <TranslationToggle />
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button onClick={() => startPractice(getDueMemoryVerses())} disabled={due.length === 0} data-testid="practice-due"
          className="font-ui flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-40">
          Practice due ({due.length})
        </button>
        <button onClick={() => startPractice(getMemoryVerses())} disabled={verses.length === 0} data-testid="practice-all"
          className="font-ui flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-muted disabled:opacity-40">
          Practice all
        </button>
        <button onClick={() => setPopOpen(true)} data-testid="popular-verses-btn"
          className="font-ui ml-auto flex items-center gap-1.5 rounded-full border border-primary/50 bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/20">
          <Star className="h-4 w-4" /> Popular verses
        </button>
        <button onClick={() => setOpen(true)} data-testid="add-verse"
          className="font-ui flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-muted">
          <Plus className="h-4 w-4" /> Add verse
        </button>
      </div>

      {verses.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border bg-background/30 p-12 text-center">
          <Brain className="mx-auto mb-3 h-8 w-8 text-primary/60" />
          <p className="font-ui text-sm text-muted-foreground">Add a verse to start hiding God’s word in your heart.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {verses.map((v) => (
            <div key={v.id} data-testid={`memory-${v.id}`} className="rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-ui text-sm font-semibold text-primary">{v.ref}</p>
                    <span className="font-ui rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Level {v.box}/5</span>
                    <span className="font-ui rounded-full border border-primary/30 px-2 py-0.5 text-xs font-semibold text-primary/80">{v.translation || "WEB"}</span>
                  </div>
                  <p className="reading-text mt-2 text-sm leading-relaxed text-foreground/80">{v.text}</p>
                </div>
                <button onClick={() => { deleteMemoryVerse(v.id); refresh(); }} data-testid={`delete-memory-${v.id}`}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="font-ui max-h-[88vh] overflow-y-auto" data-testid="verse-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Select a verse</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Pick a book, chapter and verse — no typing needed.</p>
          <div className="grid grid-cols-2 gap-3">
            <select
              data-testid="picker-book"
              value={pBook}
              onChange={(e) => { setPBook(e.target.value); setPChapter(1); }}
              className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
            >
              {books.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <select
              data-testid="picker-chapter"
              value={pChapter}
              onChange={(e) => setPChapter(parseInt(e.target.value, 10))}
              className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
            >
              {Array.from({ length: curBook?.chapters || 1 }, (_, i) => i + 1).map((c) => (
                <option key={c} value={c}>Chapter {c}</option>
              ))}
            </select>
          </div>
          <div className="max-h-[44vh] space-y-2 overflow-y-auto pr-1">
            {pVerses.map((v) => (
              <button
                key={v.v}
                data-testid={`pick-verse-${v.v}`}
                onClick={() => addFromPicker(v)}
                className="block w-full rounded-xl border border-border p-3 text-left text-sm leading-relaxed transition-colors hover:border-primary hover:bg-muted"
              >
                <span className="mr-2 font-bold text-primary">{v.v}</span>
                {v.t}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={popOpen} onOpenChange={setPopOpen}>
        <DialogContent className="font-ui max-h-[88vh] overflow-y-auto" data-testid="popular-verses-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Popular verses to memorize</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">The verses believers hide in their hearts most — tap any to add it.</p>
          <div className="mt-2 space-y-2">
            {POPULAR_VERSES.map((p) => {
              const have = haveRef(p.ref);
              return (
                <button
                  key={p.ref}
                  data-testid={`popular-${p.book}-${p.chapter}-${p.verse}`}
                  onClick={() => addPopular(p)}
                  disabled={have || adding === p.ref}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors ${
                    have ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary hover:bg-muted"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-primary">{p.ref}</span>
                    <span className="block truncate text-xs text-muted-foreground">{p.note}</span>
                  </span>
                  <span className="shrink-0">
                    {have ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : adding === p.ref ? (
                      <span className="text-xs text-muted-foreground">Adding…</span>
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
